import React, { useState, ReactElement, useContext, useEffect } from "react"
import { Button, InlineLoading, InputGroup, RadioButton } from "@wfp/ui"
import { faMinus, faPlus, faRightLong } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Select, { SingleValue } from "react-select"
import { TypeFilter, XslMappingContext } from "providers/XslMappingContext"
import { useLazyGetChoiceListQuery, useSaveChoiceMappingMutation } from "services/household/xlsFormSchemas"
import useCommon from "hooks/useCommon"
import { BaseChoice, COChoice, ChoiceField, FormField } from "interfaces/mappingInterfaces"
import OverflowText from "components/Shared/OverflowText/OverflowText"
import XlsChoiceMapping from "./XlsChoiceMapping"

type Props = {
  formFields?: FormField[]
  question?: string
}

const XlsFormChoice = ({ formFields, question }: Props): ReactElement => {
  const { mappedField } = useContext(XslMappingContext)
  const { showSnackBar } = useCommon()

  const [currentQuestion, setCurrentQuestion] = useState<SingleValue<ChoiceField> | null>(null)

  const [choiceList, setChoiceList] = useState<ChoiceField[]>([])
  const [coList, setCoList] = useState<COChoice[]>([])
  const [baseList, setBaseList] = useState<BaseChoice[]>([])
  const [questionList, setQuestionList] = useState<string[]>([])
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const [filterType, setFilterType] = useState<TypeFilter>(TypeFilter.ALL)

  const [getChoiceList] = useLazyGetChoiceListQuery()
  const [saveChoiceMapping, { isLoading }] = useSaveChoiceMappingMutation()

  const mappedLength = (): number => {
    return coList.filter(item => item.baseChoiceName).length
  }

  const handleSelect = async (value: SingleValue<ChoiceField>): Promise<void> => {
    showInfo && setShowInfo(false)
    setCurrentQuestion(value)
    setCoList([])
    setBaseList([])
    setQuestionList(choiceList?.filter(item => item.coListId === value?.coListId).map(item => item.coName) ?? [])
    if (!value?.baseListId || !value?.coListId) return
    try {
      const result = await getChoiceList({ coListId: value.coListId, baseListId: value.baseListId }).unwrap()
      setCoList(result.coChoiceMappings)
      setBaseList([
        ...result.baseListChoices,
        {
          listChoiceId: null,
          listChoiceLabel: "No matching available",
          listChoiceName: "-1",
          listId: result.baseListChoices[0]?.listId ?? 0,
        },
      ])
    } catch (error) {
      showSnackBar("Error fetching choice list", "error")
    }
  }

  const handleSave = async (): Promise<void> => {
    if (!currentQuestion?.coListId) return
    try {
      await saveChoiceMapping({ listId: currentQuestion.coListId, body: { coChoiceMappings: coList } })
      showSnackBar("Choice mapping saved", "success")
    } catch (error) {
      showSnackBar("Error saving choice mapping", "error")
    }
  }

  const transformChoiceList = (list: FormField[]): ChoiceField[] => {
    return list.map(item => ({
      coFieldId: item.fieldId,
      coName: item.fieldName,
      coListId: item.listId,
      coLabel: item.fieldLabel,
      baseName: mappedField[item.fieldName]?.baseFieldName,
      baseListId: mappedField[item.fieldName]?.baseListId,
      baseLabel: mappedField[item.fieldName]?.baseFieldLabel,
      coListName: item.listName,
      baseListName: mappedField[item.fieldName]?.baseListName,
    }))
  }

  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFilterType(e.target.value as TypeFilter)
    if (!mappedField) return

    const list = formFields?.filter(field => field?.listId && mappedField[field.fieldName]) ?? []

    switch (e.target.value) {
      case TypeFilter.UNMAPPED:
        setChoiceList(transformChoiceList(list.filter(item => !mappedField[item.fieldName].listIsMapped)))
        break
      case TypeFilter.MAPPED:
        setChoiceList(transformChoiceList(list.filter(item => mappedField[item.fieldName].listIsMapped)))
        break
      default:
        setChoiceList(transformChoiceList(list))
    }

    showInfo && setShowInfo(false)
    currentQuestion && setCurrentQuestion(null)
    setCoList([])
    setBaseList([])
  }

  useEffect(() => {
    if (mappedField)
      setChoiceList(
        transformChoiceList(formFields?.filter(field => field?.listId && mappedField[field.fieldName]) ?? []),
      )
  }, [formFields?.length, mappedField])

  useEffect(() => {
    if (question && choiceList.length) {
      const value = choiceList.find(item => item.coName.toLowerCase() === question.toLowerCase())
      handleSelect(value as SingleValue<ChoiceField>)
    }
  }, [choiceList, question])

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col w-full">
        <div className="flex items-center">
          <h1 className="text-2xl">Choice list mapping</h1>
          {currentQuestion && (
            <h1 className="text-2xl ms-2">
              - {currentQuestion.coListName}{" "}
              <FontAwesomeIcon icon={faRightLong} size="xs" color="var(--interactive-01)" />{" "}
              {currentQuestion.baseListName}
            </h1>
          )}
        </div>

        <div className="text-sm flex align-center">
          <span>
            Mapped choices: {mappedLength()} out of {coList.length}
          </span>
          <span className="ml-2">
            <div className="h-2 w-20 bg-neutral-200 rounded-md">
              <div
                className="h-2 bg-blue-400 rounded-md"
                style={{ width: `${Math.round((mappedLength() / coList.length) * 100)}%` }}
              />
            </div>
          </span>
        </div>
        <div className="mt-2 flex justify-between">
          <div className="w-6/12">
            {!question && (
              <div className="mb-1 w-10/12">
                <InputGroup>
                  <RadioButton
                    value={TypeFilter.ALL}
                    labelText="Show all"
                    checked={filterType === TypeFilter.ALL}
                    onChange={onFilterChange}
                  />
                  <RadioButton
                    value={TypeFilter.UNMAPPED}
                    labelText="Show unmapped"
                    checked={filterType === TypeFilter.UNMAPPED}
                    onChange={onFilterChange}
                  />
                  <RadioButton
                    value={TypeFilter.MAPPED}
                    labelText="Show mapped"
                    checked={filterType === TypeFilter.MAPPED}
                    onChange={onFilterChange}
                  />
                </InputGroup>
              </div>
            )}

            <div>
              <Select
                className="w-10/12"
                placeholder="Select question"
                options={choiceList}
                isClearable
                isSearchable
                value={currentQuestion}
                getOptionValue={option => option.coFieldId.toString()}
                getOptionLabel={option => option.coName}
                onChange={value => handleSelect(value as ChoiceField)}
                isDisabled={!!question}
                formatOptionLabel={opt => (
                  <div>
                    <b>{opt.coName}</b> - <span className="text-xs">{opt.coLabel}</span>
                  </div>
                )}
              />
              {currentQuestion && (
                <div className="text-sm mt-1 italic">
                  <div className="flex items-center">
                    <p>This choice list is used in {questionList.length} questions</p>
                    <FontAwesomeIcon
                      className="ms-1 p-1 cursor-pointer"
                      icon={!showInfo ? faPlus : faMinus}
                      onClick={() => setShowInfo(prev => !prev)}
                      color="var(--interactive-01)"
                    />
                  </div>
                  <p className={showInfo ? "" : "hidden"}>{questionList.join(", ")}</p>
                </div>
              )}
            </div>
          </div>

          <div className={`w-6/12 ps-4 flex ${question ? "" : "mt-4"}`}>
            {currentQuestion && (
              <div className="flex">
                <h5 className="min-w-[150px]">Base HQ Field</h5>{" "}
                <div className="mt-0.5">
                  <b>{currentQuestion?.baseName}</b>
                  <div className="text-xs table table-fixed w-full">
                    <div className="table-cell">
                      <OverflowText dark>{currentQuestion?.baseLabel}</OverflowText>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {coList.length ? (
        <>
          <div className="my-2">
            <XlsChoiceMapping coList={coList} baseList={baseList} setCoList={setCoList} />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={
                isLoading ||
                (coList.some(item => (item.baseChoiceName ?? "").trim() !== "") &&
                  coList.some(item => (item.baseChoiceName ?? "").trim() === ""))
              }
            >
              {isLoading ? <InlineLoading /> : "Save"}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  )
}

export default XlsFormChoice
